import React, { useEffect, useState } from "react";
import { Loader2, UserMinus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PostRemovalPrompt() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !user.email) return;
    // Deferred: this check only matters after leaving a household; keep it off the critical path.
    const t = setTimeout(() => {
      api.get("/subscriptions/post-removal").then((r) => { if (r.data.pending) setData(r.data); }).catch(() => {});
    }, 3000);
    return () => clearTimeout(t);
  }, [user]);

  if (!data) return null;

  const resolve = async (choice) => {
    setBusy(true);
    try {
      const r = await api.post("/subscriptions/post-removal/resolve", { choice, billing_cycle: cycle });
      toast.success(r.data.message);
      setData(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const ex = data.explainer || {};
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-5" data-testid="post-removal-prompt">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
        <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0F766E]/10"><UserMinus className="h-6 w-6 text-[#0F766E]" strokeWidth={1.5} /></span>
        <h2 className="font-display text-xl font-semibold text-[#2D3436]">You've left a Family Household</h2>
        <p className="mt-1.5 text-sm text-[#636E72]">You're on the Free plan so you're never without access to your own data. Choose how you'd like to continue.</p>
        <ul className="mt-3 space-y-1 rounded-xl bg-[#F7FAF9] p-3 text-xs text-[#636E72]">
          <li>• Records: {ex.responsibilities}</li>
          <li>• Vault: {ex.vault}</li>
          <li>• Household access: {ex.household_access}</li>
          <li>• Reminders: {ex.reminder_channels}</li>
          <li>• Features: {ex.features}</li>
        </ul>
        <div className="mt-4 flex items-center gap-2">
          <label className="text-xs font-medium text-[#636E72]">Premium billing</label>
          <select data-testid="post-removal-cycle" value={cycle} onChange={(e) => setCycle(e.target.value)}
            className="rounded-lg border border-[#E2E8F0] px-2 py-1 text-xs outline-none focus:border-[#0F766E]">
            <option value="monthly">Monthly £4.99</option>
            <option value="yearly">Annual £47.90</option>
          </select>
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <button data-testid="post-removal-premium" onClick={() => resolve("premium")} disabled={busy}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#0F766E] py-2.5 text-sm font-medium text-white hover:bg-[#0d655e] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Return to Premium"}
          </button>
          <button data-testid="post-removal-free" onClick={() => resolve("free")} disabled={busy}
            className="rounded-xl border border-[#E2E8F0] py-2.5 text-sm font-medium text-[#636E72] hover:border-[#0F766E] hover:text-[#0F766E] disabled:opacity-60">
            Continue on Free
          </button>
        </div>
      </div>
    </div>
  );
}
